export class ImagesCache {
    constructor() {
        if(!indexedDB) throw new Error('IndexedDB unsupported!');

        this.dbName = 'selpho_cache';
        this.version = 1;
        this.tableName = 'images';
        this.keyPath = 'id';

        this.request = null;
        this.db = null;
    }

    static support() {
        return !!indexedDB;
    }

    open() {
        const {dbName, version} = this;

        return new Promise((resolve, reject) => {
            this.request = indexedDB.open(dbName, version);
    
            this.request.onsuccess = (event) => {
                this.db = event.target.result;
                this.onversionchange();
                resolve(this.db);
            }
    
            this.request.onerror = reject;
            this.onupgradeneeded(resolve);
        });
    }

    /**
     * validate that indexedDb already open
     * else open it
     */
    connect() {
        return new Promise((resolve, reject) => {
            if(this.db) return resolve();

            this.open().then(resolve).catch(reject);
        });
    }

    onversionchange() {
        this.db.onversionchange = (event) => {
            this.db.close();
        }
    }

    onupgradeneeded(resolve) {
        const {request, tableName, keyPath} = this;
        request.onupgradeneeded = (event) => {
            this.db = event.target.result;
            this.db.createObjectStore(tableName, { keyPath: keyPath });

            this.onversionchange();
            resolve();
        }
    }

    storeImage(id, data) {
        const {db, tableName} = this;
        return new Promise((resolve, reject) => {
            this.connect().then(() => {
                const transaction = db.transaction([tableName], "readwrite");
                transaction.onerror = reject;
        
                // using 'put' to prevent error when try to save new data with exists key
                const put = transaction.objectStore(tableName).put({id: id, data: data});
                put.onsuccess = resolve;
                put.onerror = (e) => {
                    // if indexedDb limit exceeded
                    // clear all table
                    if(e.name === 'QuotaExceededError') {
                        return this.clearTable().then(() => {
                            this.storeImage(id, data);
                        }).catch(reject);
                    }

                    reject(e.message);
                }
            }).catch(reject);
        });

    }

    getImageById(id) {
        const {db, tableName} = this;
        
        return new Promise((resolve, reject) => {
            this.connect().then(() => {
                const transaction = db.transaction([tableName]);
                const objectStore = transaction.objectStore(tableName);
                const get = objectStore.get(id);
                
                get.onerror = reject;
                get.onsuccess = (event) => {
                    resolve(event);
                }
            }).catch(reject);
        });
    }

    getImageBlob(url) {
        const opt = {
            method: 'GET',
            mode: 'cors',
            cache: "no-cache" // because 'cach' make CORS error when image came form disk cache
        }

        return new Promise((resolve, reject) => {
            fetch(url, opt).then((res) => {
                if(res.ok) return res.blob();
                else throw new Error('get image error');
    
            }).then((blob) => {
                const reader = new FileReader();
                reader.onerror = reject;
                reader.onloadend = () => {            
                    resolve(reader.result);
                }
                reader.readAsDataURL(blob); 
            }).catch(reject);
        });
    }

    saveImageBlob(id, url) {
        return new Promise((resolve, reject) => {
            this.getImageBlob(url).then((base64) => {
                this.storeImage(id, base64).then((event) => {
                    resolve({
                        id: event.target.result,
                        data: base64
                    });
                }).catch(reject);
            }).catch(reject); 
        });
    }

    /**
     * get image from cache
     * if image not exists in the cache insert it 
     */
    get(id, imageURL) {
        return new Promise((resolve, reject) => {
            this.connect().then(() => {
                this.getImageById(id)
                    .then((event) => {
                        const data = event.target.result;
                        if(!data) throw new Error('Not found'); // go to catch and load the image

                        resolve(data);
                    })
                    .catch((err) => {
                        this.saveImageBlob(id, imageURL).then(resolve).catch(reject);
                    });
            }).catch(reject);
        });
    }

    deleteImage(id) {
        const {tableName} = this;
        return new Promise((resolve, reject) => {
            this.connect().then(() => {
                const request = this.db.transaction([tableName], "readwrite")
                    .objectStore(tableName)
                    .delete(id);

                request.onsuccess = resolve;
                request.onerror = reject;
            }).catch(reject);
        });
    }

    clearTable() {
        const {tableName} = this;
        return new Promise((resolve, reject) => {
            this.connect().then(() => {
                const clear = this.db.transaction([tableName], "readwrite").objectStore(tableName).clear();
                clear.onerror = reject;
                clear.onsuccess = resolve;
            }).catch(reject);
        });
    }
}


// WEBPACK FOOTER //
// ./src/helper/imagesCache.js